import { useEffect, useState } from 'react';
import { PALETA_BRANDING } from '@shared/constants';
import { X } from 'lucide-react';
import type { CategoriaVista } from '@shared/types';
import { NumberField } from '../ui/NumberField';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

interface Props {
  abierto: boolean;
  categoria: CategoriaVista | null;
  onClose: () => void;
  onSaved: () => void;
}

export function FormCategoria({ abierto, categoria, onClose, onSaved }: Props) {
  const [nombre, setNombre] = useState('');
  const [color, setColor] = useState<string>('#1976D2');
  const [icono, setIcono] = useState('');
  const [posicion, setPosicion] = useState<number>(0);
  const [habilitado, setHabilitado] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (categoria) {
      setNombre(categoria.nombre);
      setColor(categoria.color ?? '#1976D2');
      setIcono(categoria.icono ?? '');
      setPosicion(categoria.posicion ?? 0);
      setHabilitado(categoria.habilitado);
    } else {
      setNombre('');
      setColor('#1976D2');
      setIcono('');
      setPosicion(0);
      setHabilitado(true);
    }
    setError(null);
  }, [categoria, abierto]);

  if (!abierto) return null;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (nombre.trim().length < 1) {
      setError('El nombre es obligatorio');
      return;
    }
    setEnviando(true);
    const data = {
      nombre: nombre.trim(),
      color,
      icono: icono || undefined,
      posicion: posicion > 0 ? posicion : undefined,
      habilitado,
    };
    const res = categoria
      ? await window.api.categorias.actualizar(categoria.id, data)
      : await window.api.categorias.crear(data as Omit<CategoriaVista, 'id'>);
    setEnviando(false);
    if (res.ok) {
      onSaved();
    } else {
      setError(traducirErrorBackend(res.error, res.codigo));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold">
            {categoria ? 'Editar categoría' : 'Nueva categoría'}
          </h2>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4 px-6 py-5">
          <div>
            <label className="label" htmlFor="catNombre">Nombre *</label>
            <input
              id="catNombre"
              className="input"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <div className="label">Color</div>
            <div className="flex flex-wrap gap-2">
              {PALETA_BRANDING.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  onClick={() => setColor(c.hex)}
                  className={`h-9 w-9 rounded-full border-2 ${
                    color === c.hex ? 'border-slate-900 scale-110' : 'border-white shadow'
                  }`}
                  style={{ backgroundColor: c.hex }}
                  aria-label={c.label}
                />
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="catIcono">Icono (lucide)</label>
              <input
                id="catIcono"
                className="input"
                value={icono}
                onChange={(e) => setIcono(e.target.value)}
                placeholder="ej. beef"
              />
            </div>
            <div>
              <label className="label" htmlFor="catPos">Posición</label>
              <NumberField
                id="catPos"
                min={0}
                decimals={0}
                className="input"
                value={posicion}
                onChange={setPosicion}
              />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={habilitado}
              onChange={(e) => setHabilitado(e.target.checked)}
              className="h-4 w-4"
            />
            <span className="text-sm">Habilitado</span>
          </label>

          {error && <div className="error-text">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost" disabled={enviando}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {categoria ? 'Guardar cambios' : 'Crear categoría'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
